import {
  LoadingOverlay
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useCallback } from 'react';
import { type FileWithPath } from '@mantine/dropzone';
import { useParams } from 'react-router-dom';

import { logAnalyticsEvent } from '../../utils/analyticsUtils';
import ModalContent from '../ModalContent';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import InitializeProposalForm, { type InitializeProposalFormValues } from '../Forms/InitializeProposalForm';
import ProposalProcessingMessage from '../ProposalProcessingMessage';

interface Props {
  onClose: () => void
}

const FastDraftSetup: React.FC<Props> = (props: Props) => {
  const { onClose } = props;
  const { organization } = useParams();

  const validateFiles = useCallback((files: FileWithPath[]) => {
    if (files === null || files.length === 0) {
      return 'Please select file(s) to upload';
    }
    return null;
  }, []);

  const form = useForm({
    initialValues: {
      files: [] as FileWithPath[],
      proposalName: '',
      additionalContext: ''
    },
    validate: {
      files: validateFiles,
      proposalName: (value: string) => (value.trim().length < 1 ? 'Proposal name is required' : null)
    }
  });

  const createProposalMutation = useEnsisMutation(
    '/app/proposals',
    {
      requestType: 'post',
      contentType: 'multipart/form-data',
      showSuccessMessage: false,
      queryKeysToInvalidate: ['/app/proposals']
    }
  );

  const handleSubmitForm = useCallback((values: InitializeProposalFormValues) => {
    logAnalyticsEvent('CreateFastDraft');
    createProposalMutation.mutate({
      name: values.proposalName,
      files: values.files as unknown as string[],
      additional_context: values.additionalContext,
      organization_slug: organization ?? '',
      generate_fast_draft: true
    });
  }, [createProposalMutation.mutate, organization]);

  if (createProposalMutation.isSuccess) {
    return <ModalContent
      onClose={onClose}
      title='Generating Your Fast Draft'
      subtitle={`Your fast draft proposal is on its way! We're generating your content now and will email 
      you as soon as it's ready.`}
    >
      <ProposalProcessingMessage onClick={onClose} mb={40} />
    </ModalContent>;
  }

  return (
    <ModalContent
      title={'Let\'s Get Started'}
      subtitle='First give your proposal a name, then import an opportunity'
      secondaryButton={{
        label: 'Cancel',
        onClick: onClose
      }}
      primaryButton={{
        label: 'Next',
        onClick: form.onSubmit(handleSubmitForm),
        disabled: createProposalMutation.isPending || !form.isValid()
      }}
      onClose={onClose}
    >
      <LoadingOverlay visible={createProposalMutation.isPending} zIndex={1000} />
      <InitializeProposalForm form={form} textProps={{ fw: 500 }} />
    </ModalContent>
  );
};

export default FastDraftSetup;
